import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { FullPageLoader } from '@/components/layout/FullPageLoader';
import { NoAccess } from '@/pages/NoAccess';
import { useAuth } from '@/hooks/use-auth';
import { hasPlatformAccess } from '@/lib/auth';
import { LOGIN_PATH } from '@/lib/redirect-after-login';

/**
 * Portão do clearance. Rota-layout **acima de todos os outros layouts** — e
 * essa posição é o ponto inteiro.
 *
 * Uma guarda dentro do `RootLayout` alcançaria só `/`: o catálogo e o
 * auto-serviço têm layouts próprios, no topo da árvore, e são justamente as
 * duas superfícies que este trabalho existe para fechar. Ficariam abertas com
 * a guarda parecendo instalada.
 *
 * Três desfechos, e a ordem importa:
 *
 * 1. **Carregando** — há token e a sessão está sendo conferida. Redirecionar
 *    aqui expulsaria, a cada F5, quem está perfeitamente logado.
 * 2. **Sem sessão** → `/login`, com o destino no `state` do router (com
 *    `search` e `hash`).
 * 3. **Sessão sem acesso** → `<NoAccess />`, **nunca** redirect: o usuário já
 *    está autenticado, e mandá-lo ao login o traria de volta em loop.
 */
export function RequireAuth() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageLoader label="Verificando seu acesso" />;

  if (!isAuthenticated) {
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />;
  }

  // Espelha `require_platform` do backend (ADMIN, EMPLOYEE, BUYER) em vez de
  // recusar só `supplier`. A diferença aparece em documento sem papel e em
  // papel novo que o backend ainda não libere aqui: nos dois casos a tela
  // abriria e **todo** request responderia 401 ou 403. É melhor dizer "sem
  // acesso" do que entregar um app que carrega e não funciona.
  if (!hasPlatformAccess(user)) return <NoAccess />;

  return <Outlet />;
}
